import React, { useEffect, useState } from "react";
import { getUserFeed } from "../../api/api";
import Post from "./Post";
import Event from "./Event";
import { Skeleton } from "../ui/skeleton";

/**
 * Feed content list from getUserFeed.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {number} [props.refreshKey]
 * @returns {JSX.Element}
 */
export default function ContentList({ userId, user, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;

    async function loadFeed() {
      setLoading(true);
      setError("");
      try {
        const response = await getUserFeed(userId, 0, 20);
        if (cancelled) return;
        if (response.success) {
          setItems(Array.isArray(response.data) ? response.data : []);
        } else {
          setItems([]);
          setError(response.error || response.message || "Failed to load feed");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching feed:", err);
        setItems([]);
        setError("Failed to load feed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadFeed();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  if (!userId) {
    return (
      <p className="text-sm text-text-muted">Sign in to see your feed.</p>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-card border border-ui-border bg-ui-card p-5">
            <div className="mb-4 flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
            <Skeleton className="mb-3 h-4 w-full" />
            <Skeleton className="h-40 w-full rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-card border border-ui-border bg-ui-card p-5 text-center">
        <p className="text-compact text-error">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-card border border-ui-border bg-ui-card p-8 text-center">
        <p className="font-display text-title font-medium text-text-primary">Nothing here yet</p>
        <p className="mt-2 text-compact text-text-muted">
          Create a post to get your feed started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((item) =>
        item.type === "post" ? (
          <Post
            key={`post-${item.id}`}
            userId={userId}
            user={user}
            post={item}
            hideContent={false}
            onDeleted={() =>
              setItems((prev) =>
                prev.filter((row) => !(row.type === "post" && row.id === item.id))
              )
            }
          />
        ) : item.type === "event" ? (
          <Event
            key={`event-${item.id}`}
            userId={userId}
            user={user}
            event={item}
            hideContent={false}
            onDeleted={() =>
              setItems((prev) =>
                prev.filter((row) => !(row.type === "event" && row.id === item.id))
              )
            }
          />
        ) : (
          <div key={`unknown-${item.id}`} className="mb-4 text-sm text-text-muted">
            Unknown content type
          </div>
        )
      )}
    </div>
  );
}
